import * as Yup from 'yup'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FieldArray, Form, useField, useFormikContext } from 'formik'
import { FieldComponent } from 'components/Shared/FieldComponent'
import { PrimaryCheckbox } from 'components/Shared/PrimaryCheckbox'
import { FilesDropzone } from 'components/Shared/FilesDropzone'
import formStyles from './ContractorFormFields.module.css'
import contractorRolesOptions from 'constants/contractorRolesOptions'
import { ErrorMessage } from 'components/Shared/ErrorMessage'
import { FormColumn, FormRow } from 'components/Shared/CrudComponents'
import { uploadFile } from 'services/ContractorService'
import Sections from 'components/Contractors/sections'
import {
  CONTRACTOR_ADDITIONAL_DATA_READ,
  CONTRACTOR_ADDITIONAL_DATA_WRITE,
  CONTRACTOR_ADDRESS_READ,
  CONTRACTOR_ADDRESS_WRITE,
  CONTRACTOR_ATTACHMENTS_READ,
  CONTRACTOR_ATTACHMENTS_WRITE,
  CONTRACTOR_CORRESPONDENCE_READ,
  CONTRACTOR_CORRESPONDENCE_WRITE,
  CONTRACTOR_COST_PAYMENT_DUE_DATE_READ,
  CONTRACTOR_COST_PAYMENT_DUE_DATE_WRITE,
  CONTRACTOR_CURRENCY_READ,
  CONTRACTOR_CURRENCY_WRITE,
  CONTRACTOR_DATA_READ,
  CONTRACTOR_DATA_WRITE,
  CONTRACTOR_DEBT_LIMIT_READ,
  CONTRACTOR_DEBT_LIMIT_WRITE,
  CONTRACTOR_INCOME_PAYMENT_DUE_DATE_READ,
  CONTRACTOR_INCOME_PAYMENT_DUE_DATE_WRITE,
  CONTRACTOR_ROLES_READ,
  CONTRACTOR_ROLES_WRITE,
  useAuth,
} from 'context/auth'
import LabeledCheckboxField from 'components/Shared/LabeledCheckboxField/LabeledCheckboxField'
import { CountrySelectField } from 'components/Shared/CountrySelectField/CountrySelectField'
import { currencySchema } from 'utils/form'
import { isEqual } from 'lodash'
import { CurrencySelectField } from 'components/Shared/CurrencySelectField'
import { ReadOnlyTextField } from '../Shared/ReadOnlyTextField'

const AddressSchema = Yup.object().shape({
  street: Yup.string()
    .required('form.validation.required'),
  cityName: Yup.string()
    .required('form.validation.required'),
  country: Yup.string()
    .required('form.validation.required'),
  zipCode: Yup.string()
    .required('form.validation.required'),
})

export const ContractorSchema = Yup.object().shape({
  fullName: Yup.string()
    .required('form.validation.required')
    .matches('^[A-Z].*$', 'form.validation.mustStartWithCapitalLetter'),
  shortName: Yup.string()
    .required('form.validation.required')
    .matches('^[A-Z].*$', 'form.validation.mustStartWithCapitalLetter')
    .max(20, 'form.validation.string.tooLong'),
  companyAddress: AddressSchema,
  correspondenceAddress: AddressSchema.nullable(),
  phoneNumber: Yup.string()
    .required('form.validation.required'),
  nipNumber: Yup.string()
    .required('form.validation.required')
    .matches('^[A-Za-z0-9]*$', 'form.validation.mustContainOnlyLettersAndNumbers'),
  regonNumber: Yup.string()
    .matches('^[A-Za-z0-9]*$', 'form.validation.mustContainOnlyLettersAndNumbers')
    .nullable(),
  krsNumber: Yup.string()
    .matches('^[A-Za-z0-9]*$', 'form.validation.mustContainOnlyLettersAndNumbers')
    .nullable(),
  contactEmail: Yup.string()
    .email('form.validation.email')
    .required('form.validation.required'),
  accountingEmail: Yup.string()
    .email('form.validation.email')
    .nullable(),
  eInvoiceConsent: Yup.boolean()
    .required('form.validation.required'),
  isPartnershipSubsidiary: Yup.boolean()
    .required('form.validation.required'),
  incomePaymentDueDays: Yup.number()
    .required('form.validation.required'),
  costPaymentDueDays: Yup.number()
    .required('form.validation.required'),
  debtLimit: Yup.number()
    .required('form.validation.required'),
  currency: currencySchema,
  additionalInfo: Yup.string()
    .nullable(),
  filesIds: Yup.array()
    .of(Yup.string()),
  roles: Yup.array()
    .min(1, 'form.validation.required')
    .of(Yup.string()),
  requireCarrierVerifiedHV: Yup.boolean(),
  requireCarrierVerifiedBasic: Yup.boolean(),
})

export function ContractorFormFields({ initialFiles, requiredRoles }) {
  const { values, setFieldValue } = useFormikContext()
  const { t } = useTranslation()
  const { hasAnyPrivileges } = useAuth()
  const [correspondenceAddressSkipChecked, setCorrespondenceAddressSkipChecked] = useState(
    isEqual(values.companyAddress, values.correspondenceAddress),
  )

  const handleCorrespondenceAddressSkipCheckedChange = (event) => {
    setCorrespondenceAddressSkipChecked(event.target.checked)
  }

  useEffect(() => {
    if (values.correspondenceAddress == null) {
      setFieldValue('correspondenceAddress', { ...values.companyAddress })
      setCorrespondenceAddressSkipChecked(true)
    }
  }, [values.correspondenceAddress, values.companyAddress, setFieldValue])

  useEffect(() => {
    if (correspondenceAddressSkipChecked) {
      setFieldValue('correspondenceAddress', { ...values.companyAddress })
    }
  }, [values.companyAddress, correspondenceAddressSkipChecked, setFieldValue])

  return <Form className={'w-100'}>
    <FormRow>
      <FormColumn section={Sections.BasicDetails}
                  disabled={!hasAnyPrivileges([CONTRACTOR_DATA_WRITE])}
                  visible={hasAnyPrivileges([CONTRACTOR_DATA_READ, CONTRACTOR_ROLES_READ])}>
        <FieldComponent fieldName={'fullName'} label={'contractor.form.fullName'} />
        <FieldComponent fieldName={'shortName'} label={'contractor.form.shortName'} />
        <FieldComponent fieldName={'nipNumber'} label={'contractor.form.nipNumber'} />
        <FieldComponent fieldName={'regonNumber'} label={'contractor.form.regonNumber'} />
        <FieldComponent fieldName={'krsNumber'} label={'contractor.form.krsNumber'} />
        <FieldComponent fieldName={'phoneNumber'} label={'contractor.form.phoneNumber'} />
        <FieldComponent fieldName={'contactEmail'} label={'contractor.form.contactEmail'} />
        <FieldComponent fieldName={'accountingEmail'} label={'contractor.form.accountingEmail'} />
        {hasAnyPrivileges([CONTRACTOR_ROLES_READ]) &&
          <ContractorRoleCheckboxGroup requiredRoles={requiredRoles}
                                       disabled={!hasAnyPrivileges([CONTRACTOR_ROLES_WRITE])} />
        }
      </FormColumn>
      <FormColumn section={Sections.CompanyAddress}
                  disabled={!hasAnyPrivileges([CONTRACTOR_ADDRESS_WRITE])}
                  visible={hasAnyPrivileges([CONTRACTOR_ADDRESS_READ])}>
        <ContractorAddressFormFields fieldName={'companyAddress'} />
      </FormColumn>
      <FormColumn section={Sections.CorrespondenceAddress}
                  disabled={!hasAnyPrivileges([CONTRACTOR_CORRESPONDENCE_WRITE]) || correspondenceAddressSkipChecked}
                  visible={hasAnyPrivileges([CONTRACTOR_CORRESPONDENCE_READ])}>
        <ContractorAddressFormFields fieldName={'correspondenceAddress'} />
        <div className={formStyles.correspondenceAddressCheckbox}>
          <PrimaryCheckbox
            checked={correspondenceAddressSkipChecked}
            onChange={handleCorrespondenceAddressSkipCheckedChange}>
          </PrimaryCheckbox>
          {t('contractor.form.correspondenceAddressSkip')}
        </div>
      </FormColumn>
      <FormColumn section={Sections.AdditionalDetails}
                  disabled={!hasAnyPrivileges([CONTRACTOR_ADDITIONAL_DATA_WRITE])}
                  visible={hasAnyPrivileges([CONTRACTOR_ADDITIONAL_DATA_READ])}>
        <FieldComponent fieldName={'additionalInfo'} label={'contractor.form.additionalInfo'} multiline />
        {hasAnyPrivileges([CONTRACTOR_INCOME_PAYMENT_DUE_DATE_READ]) &&
          <FieldComponent fieldName={'incomePaymentDueDays'}
                          label={'contractor.form.incomePaymentDueDays'}
                          disabled={!hasAnyPrivileges([CONTRACTOR_INCOME_PAYMENT_DUE_DATE_WRITE])} />
        }
        {hasAnyPrivileges([CONTRACTOR_COST_PAYMENT_DUE_DATE_READ]) &&
          <FieldComponent fieldName={'costPaymentDueDays'}
                          label={'contractor.form.costPaymentDueDays'}
                          disabled={!hasAnyPrivileges([CONTRACTOR_COST_PAYMENT_DUE_DATE_WRITE])} />
        }
        {hasAnyPrivileges([CONTRACTOR_DEBT_LIMIT_READ]) &&
          <FieldComponent fieldName={'debtLimit'}
                          label={'contractor.form.debtLimit'}
                          disabled={!hasAnyPrivileges([CONTRACTOR_DEBT_LIMIT_WRITE])} />
        }
        {hasAnyPrivileges([CONTRACTOR_CURRENCY_READ]) &&
          <CurrencySelectField fieldName={'currency'}
                               label={'contractor.form.currency'}
                               disabled={!hasAnyPrivileges([CONTRACTOR_CURRENCY_WRITE])} />
        }
        <ReadOnlyTextField label={'contractor.form.hvExpirationDate'} asDateTime disabled
                           value={values.hvExpirationDate}
        />

        <ReadOnlyTextField label={'contractor.form.carrierVerificationExpirationDate'} asDateTime disabled
                           value={values.carrierVerificationExpirationDate}
        />
        <LabeledCheckboxField className="cursorPointer" fieldName={`eInvoiceConsent`}
                              label={'contractor.form.eInvoiceConsent'} />
        <LabeledCheckboxField className="cursorPointer" fieldName={`isPartnershipSubsidiary`}
                              label={'contractor.form.isPartnershipSubsidiary'} />
        <LabeledCheckboxField
          fieldName={`requireCarrierVerifiedHV`}
          disabled
          label={t('contractor.form.requireCarrierVerifiedHV')} />
        <LabeledCheckboxField
          fieldName={`requireCarrierVerifiedBasic`}
          disabled
          label={t('contractor.form.requireCarrierVerifiedBasic')} />
      </FormColumn>
      <FormColumn section={Sections.Files} className={formStyles.dropzoneSection}
                  disabled={!hasAnyPrivileges([CONTRACTOR_ATTACHMENTS_WRITE])}
                  visible={hasAnyPrivileges([CONTRACTOR_ATTACHMENTS_READ])}>
        <FilesDropzone initialFiles={initialFiles} onUpload={uploadFile}
                       onFilesUpdated={(updatedFiles) => setFieldValue('filesIds', updatedFiles.map(file => file.id))} />
      </FormColumn>
    </FormRow>
  </Form>
}

function ContractorAddressFormFields({ fieldName, disabled }) {
  const [, meta] = useField(fieldName)
  const value = meta.value
  // prevents inputs from changing their mode to uncontrolled
  if (!value) {
    return (
      <>
        <FieldComponent fieldName={'-'} label={'contractor.form.address.street'}
                        disabled />
        <FieldComponent fieldName={'-'} label={'contractor.form.address.cityName'}
                        disabled />
        <FieldComponent fieldName={'-'} label={'contractor.form.address.country'}
                        disabled />
        <FieldComponent fieldName={'-'} label={'contractor.form.address.zipCode'}
                        disabled />
      </>
    )
  }
  return (
    <>
      <FieldComponent fieldName={`${fieldName}.street`} label={'contractor.form.address.street'}
                      disabled={disabled} />
      <FieldComponent fieldName={`${fieldName}.cityName`} label={'contractor.form.address.cityName'}
                      disabled={disabled} />
      <CountrySelectField name={`${fieldName}.country`} label={'contractor.form.address.country'}
                          disabled={disabled} />
      <FieldComponent fieldName={`${fieldName}.zipCode`} label={'contractor.form.address.zipCode'}
                      disabled={disabled} />
    </>
  )
}

function ContractorRoleCheckboxGroup({ requiredRoles, disabled }) {
  const { values } = useFormikContext()
  const { t } = useTranslation()
  return <div>
    <FieldArray
      name={'roles'}
      render={arrayHelpers => (
        <>
          <h2>{t('contractor.form.roles')}</h2>
          {contractorRolesOptions.map(option => {
            const isDisabled = disabled || requiredRoles?.some(role => role === option.value)
            return (
              <div key={option.value}>
                <label className={isDisabled ? '' : 'cursorPointer'}>
                  <PrimaryCheckbox
                    disabled={isDisabled}
                    name={'roles'}
                    type="checkbox"
                    value={option.value}
                    checked={values.roles.includes(option.value)}
                    onChange={e => {
                      if (e.target.checked) arrayHelpers.push(option.value)
                      else {
                        const idx = values.roles.indexOf(option.value)
                        arrayHelpers.remove(idx)
                      }
                    }}
                  />{' '}
                  {t(option.label)}
                </label>
              </div>
            )
          })}
          <ErrorMessage name={'roles'} notRequiredTouchedToError />
        </>
      )}
    />
  </div>
}
